import type { PsHttpError } from '@patsnap/tahiti-core/common/ps-http';

interface ClassificationItem {
    code: string | number;
    description: string;
}

interface ADCItem extends ClassificationItem {
    node_list: ClassificationItem[];
}

interface TTCItem extends ClassificationItem {
    level: number;
    lang: string;
    topic_tips?: string;
}

interface GBCItem {
    code: {
        full: string;
        parent_code: string;
    };
}

type AddressItem = Record<string, {
    address?: string;
    name: string;
}[]>;

type ListItem = Record<string, string[]>;

type StandardListItem = Record<string, { name: string }[]>;

type MultilingualItem = Record<string, string>;

interface RootItem {
    name: string;
    root_name: string;
}

interface ImageItem {
    image_id: string;
    type: string;
    url: string;
}

interface EfficacyPhraseItem {
    description: string;
    type: number;
}

interface TechProPhraseItem {
    description: string;
}

interface TimelinePatent {
    patent_id: string;
    apno?: string;
    pn?: string;
    type: string[];
    is_current: boolean;
}

interface TimelineAdditional {
    pta: number;
    earliest_apdt: string;
}

interface TimelineItem {
    date: string;
    type: string[];
    patent: TimelinePatent[];
    additional_params?: TimelineAdditional;
}

interface RelatePatentItem {
    patent_id: string;
    pn: string;
}

type RelatePatent = Partial<{
    abstract: RelatePatentItem;
    claim: RelatePatentItem;
    desc: RelatePatentItem;
    image: RelatePatentItem;
}>

interface StandardSubItem {
    data_source: string;
    declarant: string[];
    declaration_date: string[];
    latest_declarant: string;
    org_standard_id: string;
    projects: string[];
    ref_id: string;
    standard_id: string;
    standard_title: string;
    standard_type: string;
    version: string;
}

interface StandardItem {
    source: string;
    standard: StandardSubItem[];
}

export interface TransferPerson {
    name: string;
    ans_id: string;
    address: string;
    exec_date?: string;
}

interface TransferItem {
    country: string;
    prsd: string;
    real_frame: string;
    receipt_date: string;
    transfer_after: TransferPerson[];
    transfer_before: TransferPerson[];
}

interface JpdcDtermItem {
    jpdc: string;
    dterm?: string[];
}

interface JpdcDterm {
    jpdc?: string[];
    dterm?: string[];
    jpdc_dterm?: JpdcDtermItem[];
}

export interface ApiGetPatentBasicRequest {
    [key: string]: unknown;
}

export interface ApiGetPatentBasicResponse {
    ABST: MultilingualItem;
    ABST_LANG: string;
    ADC: ADCItem[];
    // TODO：数据类型有问题，空的时候是空数组，有数据时是对象
    AE: StandardListItem;
    AN: ListItem;
    ANC: ListItem;
    ANCS: string[];
    ANCS_ID: string[];
    ANCS_ID_MAPPING: Record<string, { icon: boolean }>;
    ANCS_ROOT?: RootItem[];
    ANC_ADD: ListItem;
    ANS: string[];
    ANS_ROOT?: RootItem[];
    AN_ADD: ListItem;
    AN_STATE: string[][];
    APD: string;
    APN: string;
    AT: StandardListItem;
    // TODO：数据类型有问题，空的时候是空数组，有数据时是对象
    ATC: AddressItem;
    // TODO：数据类型有问题，空的时候是空字符串，有数据时是对象
    ATC_C: ListItem;
    // TODO：数据类型有问题，空的时候是空字符串，有数据时是对象
    AT_C: StandardListItem;
    AWARD: string;
    CADC: string[];
    COUNTRY: string;
    CPC: string[];
    DOCDB_IN: string[];
    EFFICACY_PHRASE: EfficacyPhraseItem[];
    EFFICIENCY_SENTENCE: string;
    FAMILY_ID: string;
    FI: string[];
    FTERM: string[];
    GBC: GBCItem[];
    // TODO：数据类型有问题，空的时候是空字符串，有数据时是对象
    GOV: MultilingualItem;
    HAS_BIO_HIGHLIGHT?: boolean;
    HAS_SPC?: boolean;
    IMAGE_ID: string;
    IN: AddressItem;
    // TODO：数据类型有问题，空的时候是空字符串，有数据时是对象
    INC: ListItem;
    INS: string[];
    IPC: string[];
    IPCR: string[];
    ISD: string;
    JPDC_DTERM: JpdcDterm;
    LANGS: string[];
    LANG_DEFAULT: string;
    LEGAL_STATUS?: number[];
    LOC: string[];
    ORIGINAL_PN: MultilingualItem;
    PATENT_ID: string;
    PATENT_TYPE: string;
    // TODO：数据类型有问题，空的时候是空字符串，有数据时是对象
    PATSNAP_IMAGE: ImageItem;
    // TODO：数据类型有问题，空的时候是空字符串，有数据时是对象
    PATSNAP_IMAGE_120: ImageItem;
    PBD: string;
    // TODO：数据类型有问题，空的时候是空数组，有数据时是对象
    PE: StandardListItem;
    PN: string;
    PV?: number;
    RELATED_PATENT: RelatePatent;
    SCORE_A?: number;
    SCORE_EA?: number;
    SCORE_EC?: number;
    SCORE_L?: number;
    SCORE_T?: number;
    SEIC: ClassificationItem[];
    TECHNICAL_PROBLEM?: string;
    TECH_PRO_PHRASE: TechProPhraseItem[];
    TITLE: MultilingualItem;
    TITLE_LANG: string;
    TRANSFER?: TransferItem[];
    TTC?: TTCItem[];
    TYPE: string[];
    UPC: string[];
    assignee_group?: string[];
    can_alert_file_wrapper: boolean;
    errata_deleted: boolean;
    high_value_patent: boolean;
    keywords: string[][];
    stemming: boolean;
    timeline: TimelineItem[];
    total_figure: number;
    valuation: number;
    STANDARD?: StandardItem[];


    // TODO：以下字段后端已经废弃
    // TODO：以下字段后端已经废弃
    // TODO：以下字段后端已经废弃
    ATC_ADD?: unknown[];
    BENEFIT_PHRASE?: unknown[];
    // TODO：以下字段前端未使用
    // TODO：以下字段前端未使用
    // TODO：以下字段前端未使用
    ABST_LANGS: string[];
    ABST_SOURCE: { isOCR: boolean; };
    EFFICIENCY_SENTENCE_LANG?: string;
    EFFICIENCY_SENTENCE_TRAN_LANG?: string;
    HAS_CHE_ABST_HIGHLIGHT?: boolean;
    PATENT_IMAGE_COUNT: number;
    PDF_IMAGE_COUNT: number;
    TECHNICAL_PROBLEM_LANG?: string;
    TECHNICAL_PROBLEM_TRAN_LANG?: string;
    THUMB_TYPE: string;
    TITLE_LANGS: string[];
    title_default: string;
    PRO_13B_SUMMARY?: string;
    PRO_13B_SUMMARY_LANG?: string;
    PRO_13B_SUMMARY_TRAN?: string;
    PRO_13B_SUMMARY_TRAN_LANG?: string;
    EFFECT_13B_SUMMARY?: string;
    EFFECT_13B_SUMMARY_LANG?: string;
    EFFECT_13B_SUMMARY_TRAN?: string;
    EFFECT_13B_SUMMARY_TRAN_LANG?: string;
}

export const GetPatentBasic = Symbol.for('GetPatentBasic');

export interface GetPatentBasic {
    getPatentBasic(request: ApiGetPatentBasicRequest): Promise<ApiGetPatentBasicResponse | PsHttpError>;
}
